import {
  Badge,
  Box,
  Flex,
  Heading,
  HStack,
  Stack,
  Text,
} from "@chakra-ui/react";
import { NotInRoundBadge } from "./NotInRoundBadge";
import { ConfirmedUnchangedBadge } from "./ConfirmedUnchangedBadge";
import { useTranslation } from "react-i18next";
import { FiAward } from "react-icons/fi";
import { BidStatus, type EvalBidDto } from "../../../autogen/bff-api";
import { hasAnyScore, isRejectedBid, isUnrankedBid, sortAwardBids } from "../award/AwardBidsTable";
import { MinimumRequirementOutcomeBadge } from "../minimum-requirements/MinimumRequirementOutcomeBadge";
import { formatMoney } from "../../../common/formatting/displaySum";

export const EvaluationRanking = ({
  bids,
  awarded = false,
}: {
  bids: EvalBidDto[];
  awarded?: boolean;
}) => {
  const { t } = useTranslation();
  const ranked = sortAwardBids(bids);

  return (
    <Box>
      <Heading size="sm" mb="3">
        {t("Ranking")}
      </Heading>
      <Stack spacing="2">
        {ranked.map((bid) => {
          const isRejected = isRejectedBid(bid);
          const isUnranked = isUnrankedBid(bid);
          // Once awarded, the actual decision outranks the computed ranking (the buyer
          // may have chosen manually, e.g. private price evaluation).
          const isWinner = awarded
            ? bid.status === BidStatus.Awarded
            : bid.rank === 1 && !isRejected;
          return (
            <Flex
              key={bid.bidId}
              align="center"
              gap="4"
              px="5"
              py="3"
              rounded="lg"
              border="1px solid"
              borderColor={
                isRejected ? "red.200" : isWinner ? "green.200" : "smBorder"
              }
              bg={
                isRejected ? "red.50" : isWinner ? "green.50" : "smBackground"
              }
            >
              <Flex
                align="center"
                justify="center"
                boxSize="9"
                rounded="full"
                bg={
                  isRejected
                    ? "red.100"
                    : isWinner
                      ? "smPrimary"
                      : "smBackgroundSecondary"
                }
                color={isRejected ? "red.700" : isWinner ? "white" : "smMuted"}
                fontWeight="bold"
                flexShrink={0}
              >
                {isRejected || isUnranked ? "–" : bid.rank}
              </Flex>
              <Box flex="1">
                <HStack spacing="2" align="center">
                  <Text fontWeight="semibold" color="smText">
                    {bid.supplierName || t("Unknown")}
                  </Text>
                  {isRejected && <Badge colorScheme="red">{t("Avvist")}</Badge>}
                  {isUnranked && <Badge colorScheme="gray">{t("Not ranked")}</Badge>}
                  <NotInRoundBadge notInRound={bid.notInRound} />
                  <ConfirmedUnchangedBadge confirmedUnchanged={bid.confirmedUnchanged} />
                  {!isRejected && (
                    <MinimumRequirementOutcomeBadge minimumRequirements={bid.minimumRequirements} />
                  )}
                  {isWinner && awarded && <Badge colorScheme="green">{t("Selected")}</Badge>}
                </HStack>
                {bid.effectivePrice?.value != null && (
                  <HStack spacing="2" align="baseline">
                    <Text
                      fontSize="sm"
                      color={bid.priceOverride ? "smPrimary" : "smMuted"}
                      fontWeight={bid.priceOverride ? "semibold" : "normal"}
                    >
                      {formatMoney(bid.effectivePrice.value)}{" "}
                      {bid.effectivePrice.currency}
                    </Text>
                    {bid.priceOverride && bid.price?.value != null && (
                      <Text
                        fontSize="xs"
                        color="smMuted"
                        textDecoration="line-through"
                        aria-label={
                          t(
                            "Supplier's submitted price: {{amount}} {{currency}}",
                            {
                              amount: formatMoney(bid.price.value),
                              currency: bid.price.currency,
                            },
                          ) ?? ""
                        }
                      >
                        {formatMoney(bid.price.value)}{" "}
                        {bid.price.currency}
                      </Text>
                    )}
                  </HStack>
                )}
              </Box>
              <Text
                fontWeight="bold"
                fontFamily="mono"
                color={
                  isRejected ? "red.700" : isWinner ? "smPrimary" : "smText"
                }
              >
                {hasAnyScore(bid) ? bid.weightedTotal.toFixed(2) : "–"}
              </Text>
              {isWinner && <Box as={FiAward} color="green.500" boxSize="5" />}
            </Flex>
          );
        })}
      </Stack>
    </Box>
  );
};
